import React from 'react';
import { HStack, Button, Image, Box } from '@chakra-ui/react';
import SignaturePad from 'signature_pad';

export default ({
	onSign = () => {},
	src = '',
	isDisabled = false,
	autoClear = false,
	defaultEdit = false,
	height = 200,
	navDrawerEnabled = false,
	navDrawerOpen = false
}) => {
	const [editSignature, setEditSignature] = React.useState(defaultEdit);
	const [signaturePad, setSignaturePad] = React.useState(null);
	const canvas = React.useRef(null);

	// Adjust canvas coordinate space taking into account pixel ratio,
	// to make it look crisp on mobile devices.
	// This also causes canvas to be cleared.
	const resizeCanvas = () => {
		if (canvas.current) {
			// When zoomed out to less than 100%, for some very strange reason,
			// some browsers report devicePixelRatio as less than 1
			// and only part of the canvas is cleared then.
			const ratio = Math.max(window.devicePixelRatio || 1, 1);
			canvas.current.width = canvas.current.offsetWidth * ratio;
			canvas.current.height = canvas.current.offsetHeight * ratio;
			canvas.current.getContext('2d').scale(ratio, ratio);
		}
	};

	React.useEffect(() => {
		if (editSignature) {
			setSignaturePad(new SignaturePad(canvas.current));

			resizeCanvas();
			window.addEventListener('resize', resizeCanvas);

			return () => window.removeEventListener('resize', resizeCanvas);
		}
	}, [editSignature]);

	React.useEffect(() => {
		setTimeout(resizeCanvas, 400);
	}, [navDrawerEnabled, navDrawerOpen]);

	/**
	 * Crop signature canvas to only contain the signature and no whitespace.
	 *
	 * @since 1.0.0
	 */
	const trimCanvas = () => {
		// First duplicate the canvas to not alter the original
		const croppedCanvas = document.createElement('canvas');
		const croppedCtx = croppedCanvas.getContext('2d');

		croppedCanvas.width = canvas.current.width;
		croppedCanvas.height = canvas.current.height;
		croppedCtx.drawImage(canvas.current, 0, 0);

		// Next do the actual cropping
		let w = croppedCanvas.width,
			h = croppedCanvas.height,
			pix = { x: [], y: [] },
			imageData = croppedCtx.getImageData(0, 0, croppedCanvas.width, croppedCanvas.height),
			x,
			y,
			index;

		for (y = 0; y < h; y++) {
			for (x = 0; x < w; x++) {
				index = (y * w + x) * 4;
				if (imageData.data[index + 3] > 0) {
					pix.x.push(x);
					pix.y.push(y);
				}
			}
		}
		pix.x.sort(function (a, b) {
			return a - b;
		});
		pix.y.sort(function (a, b) {
			return a - b;
		});
		const n = pix.x.length - 1;

		w = pix.x[n] - pix.x[0];
		h = pix.y[n] - pix.y[0];
		const cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

		croppedCanvas.width = w;
		croppedCanvas.height = h;
		croppedCtx.putImageData(cut, 0, 0);

		return croppedCanvas.toDataURL();
	};

	return editSignature ? (
		<>
			<canvas style={{ width: '100%', height }} ref={canvas} />
			{signaturePad && (
				<HStack w="100%" my="8px">
					<Button onClick={() => signaturePad.clear()} data-action="clear">
						Clear
					</Button>
					<Button onClick={() => setEditSignature(false)} data-action="cancel">
						Cancel
					</Button>
					<Button
						data-action="back"
						onClick={() => {
							const data = signaturePad.toData();

							if (data) {
								data.pop(); // remove the last dot or line
								signaturePad.fromData(data);
							}
						}}
					>
						Undo
					</Button>
					<Button
						data-action="save"
						onClick={() => {
							if (signaturePad.isEmpty()) {
								onSign('');
							} else {
								onSign(trimCanvas());
							}

							if (autoClear) {
								signaturePad.clear();
							}

							setEditSignature(false);
						}}
					>
						Save
					</Button>
				</HStack>
			)}
		</>
	) : (
		<>
			<Box h={200}>
				<Image maxH="100%" src={src} />
			</Box>
			<Button size="lg" w="100%" my="8px" disabled={isDisabled} onClick={() => setEditSignature(true)}>
				Add Signature
			</Button>
		</>
	);
};
